import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import notificationService, { AppNotification } from '../services/notificationService';

const POLL_INTERVAL_MS = 30_000;

const NotificationBell: React.FC = () => {
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifications, setNotifications] = useState<AppNotification[]>([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    /** The panel is portalled out of this subtree, so outside-click must test it too. */
    const panelRef = useRef<HTMLDivElement>(null);
    /**
     * Viewport coords for the panel. The sidebar is md:static inside the shell's
     * overflow-hidden, which clips an absolutely-positioned child at the sidebar
     * edge — so the panel is fixed and anchored to the button instead.
     */
    const [panelPos, setPanelPos] = useState<{ left: number; bottom: number } | null>(null);
    /** Matches Tailwind's md breakpoint; below it the panel is full-width instead. */
    const [isDesktop, setIsDesktop] = useState(
        () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches,
    );

    useEffect(() => {
        const mq = window.matchMedia('(min-width: 768px)');
        const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);
    const navigate = useNavigate();
    const location = useLocation();

    const fetchUnreadCount = useCallback(async () => {
        try {
            const count = await notificationService.getUnreadCount();
            setUnreadCount(count);
        } catch {
            // ignore — trainer may not be logged in yet
        }
    }, []);

    const fetchNotifications = useCallback(async () => {
        try {
            setLoading(true);
            const data = await notificationService.getMyNotifications();
            // Newest 10, plus any older unread ones: they count towards "N new",
            // so they must stay in the list to be clickable.
            setNotifications(data.filter((n, i) => i < 10 || !n.isRead));
            setUnreadCount(data.filter((n) => !n.isRead).length);
        } catch {
            // ignore
        } finally {
            setLoading(false);
        }
    }, []);

    // Poll unread count every 30s
    useEffect(() => {
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [fetchUnreadCount]);

    // Re-fetch count on window focus
    useEffect(() => {
        window.addEventListener('focus', fetchUnreadCount);
        // Replying clears that entry's notifications on the server.
        window.addEventListener('comment-posted', fetchUnreadCount);
        return () => {
            window.removeEventListener('focus', fetchUnreadCount);
            window.removeEventListener('comment-posted', fetchUnreadCount);
        };
    }, [fetchUnreadCount]);

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            const t = e.target as Node;
            if (
                dropdownRef.current && !dropdownRef.current.contains(t) &&
                panelRef.current && !panelRef.current.contains(t)
            ) {
                setOpen(false);
            }
        };
        if (open) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const handleBellClick = () => {
        if (!open) {
            fetchNotifications();
            const rect = dropdownRef.current?.getBoundingClientRect();
            if (rect) {
                setPanelPos({
                    left: rect.right + 8,
                    bottom: Math.max(8, window.innerHeight - rect.bottom),
                });
            }
        }
        setOpen((prev) => !prev);
    };

    const handleNotificationClick = async (n: AppNotification) => {
        if (!n.isRead) {
            await notificationService.markAsRead(n.id);
            setNotifications((prev) =>
                prev.map((item) => (item.id === n.id ? { ...item, isRead: true } : item))
            );
            setUnreadCount((prev) => Math.max(0, prev - 1));
        }
        setOpen(false);
        if (n.relatedEntryId && n.relatedUserId) {
            // Already on a training plan: let that page ask to save its edits
            // first, since navigating there would wipe them.
            if (/^\/users\/[^/]+\/training/.test(location.pathname)) {
                window.dispatchEvent(new CustomEvent('open-training-notification', {
                    detail: { userId: n.relatedUserId, entryId: n.relatedEntryId, weekDate: n.relatedDate, message: n.message },
                }));
                return;
            }
            navigate(`/users/${n.relatedUserId}/training`, {
                state: { openEntryId: n.relatedEntryId, weekDate: n.relatedDate },
            });
        }
    };

    const handleMarkAllRead = async () => {
        await notificationService.markAllRead();
        setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
        setUnreadCount(0);
    };

    const formatTime = (iso: string) => {
        const d = new Date(iso);
        const now = new Date();
        const diffMs = now.getTime() - d.getTime();
        const diffMin = Math.floor(diffMs / 60000);
        if (diffMin < 1) return 'Just now';
        if (diffMin < 60) return `${diffMin}m ago`;
        const diffH = Math.floor(diffMin / 60);
        if (diffH < 24) return `${diffH}h ago`;
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    return (
        <div ref={dropdownRef} className="relative px-4 py-1 border-b border-slate-700">
            <button
                onClick={handleBellClick}
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-slate-300 hover:bg-slate-700/50 hover:text-white transition-all group"
            >
                <div className="w-8 flex justify-center relative">
                    <i className="fa-solid fa-bell text-lg text-slate-400 group-hover:text-cyan-500 transition-colors"></i>
                    {unreadCount > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 shadow-lg">
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                    )}
                </div>
                <span className="font-medium">Notifications</span>
            </button>

            {/* Dropdown */}
            {open && createPortal(
                <div
                    ref={panelRef}
                    // Fixed, not absolute: the shell clips overflow, and on mobile a
                    // panel to the right of the 288px drawer would be off-screen.
                    style={
                        panelPos && isDesktop
                            ? { left: panelPos.left, bottom: panelPos.bottom }
                            : undefined
                    }
                    className="fixed left-2 right-2 bottom-2 md:right-auto md:w-80 max-h-[50dvh] md:max-h-[70dvh] flex flex-col bg-white rounded-xl shadow-2xl border border-slate-200 z-[100] overflow-hidden"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-cyan-500 to-blue-500 flex-shrink-0">
                        <div className="flex items-center gap-2">
                            <i className="fa-solid fa-bell text-white text-sm"></i>
                            <span className="text-white font-bold text-sm">Notifications</span>
                            {unreadCount > 0 && (
                                <span className="bg-white text-cyan-600 text-xs font-bold px-2 py-0.5 rounded-full">
                                    {unreadCount} new
                                </span>
                            )}
                        </div>
                        {unreadCount > 0 && (
                            <button
                                onClick={handleMarkAllRead}
                                className="text-white/80 hover:text-white text-xs font-medium transition-colors"
                            >
                                Mark all read
                            </button>
                        )}
                    </div>

                    {/* List */}
                    <div className="flex-1 min-h-0 md:max-h-80 overflow-y-auto divide-y divide-slate-50">
                        {loading ? (
                            <div className="flex items-center justify-center py-10 text-slate-400">
                                <i className="fa-solid fa-spinner fa-spin text-xl"></i>
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-10 text-slate-400 gap-2">
                                <i className="fa-regular fa-bell-slash text-2xl"></i>
                                <p className="text-sm font-medium">No notifications yet</p>
                            </div>
                        ) : (
                            notifications.map((n) => (
                                <button
                                    key={n.id}
                                    onClick={() => handleNotificationClick(n)}
                                    className={`w-full flex items-start gap-3 px-4 py-2.5 md:py-3 text-left hover:bg-slate-50 transition-colors ${!n.isRead ? 'bg-cyan-50/60' : ''}`}
                                >
                                    <div className={`mt-0.5 w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${!n.isRead ? 'bg-cyan-100' : 'bg-slate-100'}`}>
                                        <i className={`fa-solid fa-comments text-sm ${!n.isRead ? 'text-cyan-500' : 'text-slate-400'}`}></i>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm leading-snug ${!n.isRead ? 'font-semibold text-slate-800' : 'text-slate-600'}`}>
                                            {n.message}
                                        </p>
                                        <p className="text-xs text-slate-400 mt-0.5">{formatTime(n.createdAt)}</p>
                                    </div>
                                    {!n.isRead && (
                                        <div className="mt-1.5 w-2 h-2 rounded-full bg-cyan-500 flex-shrink-0"></div>
                                    )}
                                </button>
                            ))
                        )}
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
};

export default NotificationBell;





